import { z } from 'zod'
import type { CalculatorDefinition } from '@/types/calculator'
import { positiveMoney } from '@/calculators/shared/schema'
import { calculateSalaryIncrease } from './calculation'
import { content, faqs } from './content'
import type { SalaryIncreaseInputs, SalaryIncreaseResult } from './types'

const schema = z.object({
  currentSalary: positiveMoney('Enter a current salary'),
  salaryPeriod: z.enum(['monthly', 'annual']),
  increasePercent: z.number().min(0).max(100),
  bonusValue: positiveMoney(),
  bonusType: z.enum(['amount', 'percent']),
  includeTaxEstimate: z.boolean(),
  annualGrossForTax: positiveMoney(),
})

const defaults: SalaryIncreaseInputs = {
  currentSalary: 35_000,
  salaryPeriod: 'monthly',
  increasePercent: 5,
  bonusValue: 0,
  bonusType: 'amount',
  includeTaxEstimate: false,
  annualGrossForTax: 420_000,
}

export const salaryIncreaseDefinition: CalculatorDefinition<
  SalaryIncreaseInputs,
  SalaryIncreaseResult
> = {
  id: 'salary-increase',
  slug: 'salary-increase-calculator',
  category: 'salary',
  title: 'Salary Increase & Bonus Calculator',
  shortTitle: 'Salary Increase',
  description:
    'See the effect of a raise or bonus on your South African gross salary, and get an optional tax-aware net estimate.',
  inputs: [
    {
      name: 'currentSalary',
      label: 'Current salary',
      type: 'money',
      required: true,
      placeholder: 'e.g. 35 000',
      unit: 'R',
    },
    {
      name: 'salaryPeriod',
      label: 'Salary period',
      type: 'radio',
      required: true,
      options: [
        { value: 'monthly', label: 'Monthly' },
        { value: 'annual', label: 'Annual' },
      ],
      defaultValue: 'monthly',
    },
    {
      name: 'increasePercent',
      label: 'Salary increase',
      type: 'percentage',
      required: true,
      min: 0,
      max: 100,
      step: 0.1,
      unit: '%',
      defaultValue: 5,
    },
    {
      name: 'bonusValue',
      label: 'Bonus amount or percent',
      type: 'money',
      required: false,
      placeholder: '0',
      unit: 'R / %',
    },
    {
      name: 'bonusType',
      label: 'Bonus is a',
      type: 'radio',
      required: true,
      options: [
        { value: 'amount', label: 'Amount' },
        { value: 'percent', label: 'Percent of salary' },
      ],
      defaultValue: 'amount',
    },
    {
      name: 'includeTaxEstimate',
      label: 'Estimate tax impact',
      type: 'checkbox',
      defaultValue: false,
    },
    {
      name: 'annualGrossForTax',
      label: 'Annual gross for tax context',
      type: 'money',
      required: false,
      placeholder: 'e.g. 420 000',
      unit: 'R',
      hint: 'Used only if you enable the tax estimate',
    },
  ],
  defaults,
  schema,
  calculate: calculateSalaryIncrease,
  resultSections: [
    {
      id: 'newMonthlyGross',
      label: 'New monthly gross',
      format: 'currency',
      value: (r: SalaryIncreaseResult) => r.newMonthlyGross,
      highlighted: true,
    },
    {
      id: 'monthlyGrossIncrease',
      label: 'Monthly gross increase',
      format: 'currency',
      value: (r: SalaryIncreaseResult) => r.monthlyGrossIncrease,
      highlighted: true,
    },
    {
      id: 'bonusMonthly',
      label: 'Bonus (monthly equivalent)',
      format: 'currency',
      value: (r: SalaryIncreaseResult) => r.bonusMonthly,
    },
    {
      id: 'estimatedNetMonthlyIncrease',
      label: 'Estimated net monthly increase',
      format: 'currency',
      value: (r: SalaryIncreaseResult) => r.estimatedNetMonthlyIncrease,
    },
    {
      id: 'newAnnualGross',
      label: 'New annual gross',
      format: 'currency',
      value: (r: SalaryIncreaseResult) => r.newAnnualGross,
    },
    {
      id: 'annualGrossIncrease',
      label: 'Annual gross increase',
      format: 'currency',
      value: (r: SalaryIncreaseResult) => r.annualGrossIncrease,
    },
    {
      id: 'taxOnIncrease',
      label: 'Estimated tax on increase',
      format: 'currency',
      value: (r: SalaryIncreaseResult) => r.taxOnIncrease,
    },
  ],
  assumptions: [
    {
      name: 'SARS income tax tables for individuals 2026/2027',
      url: 'https://www.sars.gov.za/tax-rates/income-tax/rates-of-tax-for-individuals/',
      reviewedAt: '2026-09-18',
      effectiveFrom: '2026-03-01',
      effectiveTo: '2027-02-28',
      version: '2027',
      notes:
        'Tax estimate uses the primary rebate only and assumes the increase is taxed at your marginal rate based on the supplied annual gross.',
    },
  ],
  related: ['salary-calculator', 'bond-calculator', 'car-affordability-calculator'],
  seo: {
    title: 'Salary Increase & Bonus Calculator South Africa',
    description:
      'See the gross and estimated net pay after a salary increase or bonus with this South African salary increase calculator.',
    keywords: [
      'salary increase calculator',
      'bonus tax South Africa',
      'raise calculator',
      'gross to net',
    ],
  },
  content,
  faqs,
}
